import { WIDGET_WIDTH } from "@shared/constants";

import { CustomStyles, MessageType } from "../types";

import {
    getState,
    setState,
    setReactiveState,
    getLastMergedConfig,
    getWindowOrigin,
    isPreloaded,
    type WidgetContext,
} from "./state";
import { EMBED_INITIAL_HEIGHT, removeEmbedSkeleton } from "./skeleton";
import { removeExistingIframes } from "./iframe";
import { sendShow } from "./messaging";

const embedCleanupMap = new WeakMap<HTMLIFrameElement, () => void>();

/** Tear down all EMBED positioning wiring for an iframe: the scroll/resize/ResizeObserver cleanup stored
 *  in embedCleanupMap, plus the EVENT_RESIZE message handler. No-op when none is attached. */
const teardownEmbedListeners = (iframe: HTMLIFrameElement | null, ctx: WidgetContext): void => {
    const state = getState(ctx);
    if (state.resizeHandler) {
        window.removeEventListener("message", state.resizeHandler);
        setState({ resizeHandler: null }, ctx);
    }
    if (iframe) {
        embedCleanupMap.get(iframe)?.();
        embedCleanupMap.delete(iframe);
    }
};

// ============================================
// MODAL
// ============================================

export const showModal = (
    iframe: HTMLIFrameElement,
    windowOrigin: string,
    ctx: WidgetContext,
    zIndex?: number,
): void => {
    // If this iframe was previously an EMBED, tear its positioning listeners down before taking it over as
    // MODAL — otherwise they fight the full-screen styles by repositioning over the removed placeholder.
    teardownEmbedListeners(iframe, ctx);

    iframe.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100dvw;
        height: 100dvh;
        border: none;
        z-index: ${zIndex ?? 10000};
        color-scheme: light;
    `;
    iframe.removeAttribute("aria-hidden");
    iframe.removeAttribute("tabindex");
    iframe.setAttribute("scrolling", "no");

    // Ensure iframe is in body
    if (iframe.parentElement !== document.body) {
        document.body.appendChild(iframe);
    }

    sendShow(iframe, windowOrigin);
    setReactiveState({ isOpen: true }, ctx);
};

// ============================================
// EMBED
// ============================================

export const showEmbed = (
    iframe: HTMLIFrameElement,
    config: {
        targetElementId?: string | undefined;
        windowOrigin: string;
        zIndex?: number;
    },
    ctx: WidgetContext,
): Window | null => {
    const { targetElementId, windowOrigin, zIndex } = config;

    // Re-show fast path — but ONLY while our tracked placeholder is still live in the document. When a client
    // embeds <HallidayEmbed> inside their own modal (Provider outside), unmounting the modal detaches the
    // placeholder and later remounts the target with brand-new DOM nodes. Trusting embedCleanupMap then would
    // reposition the warm iframe over the detached placeholder's 0x0 rect — visible:true but invisible.
    // getElementById returns ONLY connected nodes, so a null result ⇔ our subtree was detached (stale) ⇒
    // tear the dead wiring down and rebuild against the fresh target below. HARD CONSTRAINT: the warm iframe
    // is NEVER reparented (reparenting reloads its document); only the placeholder + listeners are rebuilt.
    let isRebuild = false;
    if (embedCleanupMap.has(iframe)) {
        const livePlaceholder =
            document.getElementById(ctx.ids.wrapper) || document.getElementById(ctx.ids.placeholder);
        if (livePlaceholder) {
            // Placeholder still connected → cheap re-show: just flip CSS.
            iframe.style.visibility = "visible";
            iframe.style.opacity = "1";
            iframe.style.pointerEvents = "auto";
            iframe.style.zIndex = String(zIndex ?? 1);
            iframe.removeAttribute("aria-hidden");
            iframe.removeAttribute("tabindex");
            // Trigger the still-active resize listener to reposition iframe over placeholder
            window.dispatchEvent(new Event("resize"));
            sendShow(iframe, windowOrigin);
            setReactiveState({ isOpen: true }, ctx);
            return iframe.contentWindow;
        }
        // Stale: the tracked placeholder was detached (client modal torn down). Tear the dead EMBED wiring
        // down and fall through (no early return) to the full setup, which rebuilds the placeholder + all
        // listeners against the current DOM.
        teardownEmbedListeners(iframe, ctx);
        isRebuild = true;
    }

    if (!targetElementId) {
        console.error("[HallidayPayments] EMBED mode requires targetElementId in openHallidayPayments");
        return null;
    }

    const targetElement = document.getElementById(targetElementId);
    if (!targetElement) {
        console.error(`[HallidayPayments] Target element '${targetElementId}' not found`);
        return null;
    }

    const state = getState(ctx);

    // Use existing wrapper (from skeleton) or create placeholder
    let placeholder =
        document.getElementById(ctx.ids.wrapper) ||
        document.getElementById(ctx.ids.placeholder);
    if (!placeholder) {
        placeholder = document.createElement("div");
        placeholder.id = ctx.ids.placeholder;
        placeholder.style.cssText = `
            width: ${WIDGET_WIDTH}px;
            max-width: 100%;
            height: ${state.lastKnownHeight || EMBED_INITIAL_HEIGHT}px;
        `;
        targetElement.appendChild(placeholder);
    }

    // Position iframe over the placeholder using fixed positioning
    const updateIframePosition = () => {
        const rect = placeholder!.getBoundingClientRect();
        iframe.style.position = "fixed";
        iframe.style.top = rect.top + "px";
        iframe.style.left = rect.left + "px";
        iframe.style.width = rect.width + "px";
        iframe.style.height = rect.height + "px";
        iframe.style.border = "none";
        iframe.style.zIndex = String(zIndex ?? 1);
    };

    // Force browser to layout
    void placeholder.offsetHeight;

    // Position-before-visible: updateIframePosition() MUST run before the visibility flip.
    const reveal = () => {
        updateIframePosition();
        iframe.style.visibility = "visible";
        iframe.style.opacity = "1";
        iframe.style.pointerEvents = "auto";
    };
    if (isRebuild) {
        // Rebuild path: the iframe is already warm and lastKnownHeight is known, so the placeholder settles
        // synchronously (the forced layout above). Position + reveal in the SAME frame — the double-rAF would
        // add ~2 blank frames, and on a preloaded reopen there's no skeleton to mask them (visible flicker).
        reveal();
    } else {
        // First show: double-rAF for paint-ready positioning before the initial reveal.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                reveal();
            });
        });
    }

    // ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
        updateIframePosition();
    });
    resizeObserver.observe(placeholder);

    // Scroll listener (capture phase)
    const scrollHandler = () => updateIframePosition();
    window.addEventListener("scroll", scrollHandler, true);

    // Resize listener
    const resizeHandler = () => updateIframePosition();
    window.addEventListener("resize", resizeHandler);

    // Store cleanup for this iframe
    embedCleanupMap.set(iframe, () => {
        resizeObserver.disconnect();
        window.removeEventListener("scroll", scrollHandler, true);
        window.removeEventListener("resize", resizeHandler);
        placeholder?.remove();
    });

    // Handle resize messages for placeholder height
    const handleResize = (event: MessageEvent) => {
        if (event.origin !== windowOrigin) return;
        if (event.source !== iframe.contentWindow) return;

        const { type, payload } = event.data || {};
        if (type === MessageType.EVENT_RESIZE && payload) {
            if (payload.height !== undefined && placeholder) {
                setState({ lastKnownHeight: payload.height }, ctx);
                placeholder.style.height = payload.height + "px";
                updateIframePosition();
            }
        }
    };
    window.addEventListener("message", handleResize);
    setState({ resizeHandler: handleResize }, ctx);

    iframe.removeAttribute("aria-hidden");
    iframe.removeAttribute("tabindex");

    // Skeleton removal: if already preloaded, remove immediately
    if (isPreloaded(ctx)) {
        removeEmbedSkeleton(ctx);
    }
    // If not yet preloaded, the pipeline's onReady wrapper calls removeEmbedSkeleton on WIDGET_READY

    sendShow(iframe, windowOrigin);
    setReactiveState({ isOpen: true }, ctx);

    return iframe.contentWindow;
};

// ============================================
// Dispatcher
// ============================================

export const showWidget = (
    openParams: {
        windowType?: string | undefined;
        targetElementId?: string | undefined;
        windowOrigin?: string | undefined;
        customStyles?: CustomStyles | undefined;
        [key: string]: unknown;
    },
    _options: { widgetUrl?: string } = {},
    ctx: WidgetContext,
): Window | null => {
    const windowType = openParams.windowType || "MODAL";
    const windowOrigin = (openParams.windowOrigin || "") as string;
    const zIndex = openParams.customStyles?.zIndex;
    const iframe = getState(ctx).iframe;

    // WindowType is EMBED/MODAL only — the dead widget-mode POPUP branch has been removed.
    if (!iframe) return null;

    if (windowType === "EMBED") {
        return showEmbed(
            iframe,
            {
                targetElementId: openParams.targetElementId,
                windowOrigin,
                ...(zIndex !== undefined && { zIndex }),
            },
            ctx,
        );
    }

    // Default: MODAL
    showModal(iframe, windowOrigin, ctx, zIndex);
    return iframe.contentWindow;
};

// ============================================
// Visibility Detection & Ensure Visible
// ============================================

export const isWidgetVisible = (ctx: WidgetContext): boolean => {
    const { iframe } = getState(ctx);
    // MODAL/EMBED (widget-mode POPUP arm removed)
    if (!iframe) return false;
    return iframe.getAttribute("aria-hidden") !== "true";
};

export const ensureVisible = (ctx: WidgetContext): void => {
    if (isWidgetVisible(ctx)) return;

    const config = getLastMergedConfig(ctx) ?? {};
    showWidget(
        {
            windowType: config.windowType as string | undefined,
            targetElementId: config.targetElementId as string | undefined,
            windowOrigin: getWindowOrigin(ctx) ?? "",
            customStyles: config.customStyles as CustomStyles | undefined,
        },
        undefined,
        ctx,
    );
};

// ============================================
// Hide / Destroy
// ============================================

export const hideWidget = (ctx: WidgetContext): void => {
    const state = getState(ctx);

    // Handle iframe hide
    const iframe = state.iframe;
    if (!iframe) return;

    iframe.style.visibility = "hidden";
    iframe.style.opacity = "0";
    iframe.style.pointerEvents = "none";
    iframe.style.zIndex = "-1";
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");

    // Reflect visibility in the reactive store and notify the close observer. Both close()
    // and a widget-initiated EVENT_WINDOW_CLOSE reach here, so the "close" event fires for either.
    setReactiveState({ isOpen: false }, ctx);
    ctx.onClose?.();
};

export const destroyDisplay = (ctx: WidgetContext): void => {
    const state = getState(ctx);

    // Tear down EMBED positioning listeners + the EVENT_RESIZE message handler (shared with showModal's
    // MODAL-takeover path).
    teardownEmbedListeners(state.iframe, ctx);

    // Remove all known iframes and DOM elements (this instance only)
    removeExistingIframes(ctx);

    // Remove wrapper
    const wrapper = document.getElementById(ctx.ids.wrapper);
    if (wrapper) wrapper.remove();
};
